import React from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { X, Loader2 } from 'lucide-react';

// ─── Button ──────────────────────────────────────────────────
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  colorScheme?: 'admin' | 'principal' | 'teacher' | 'parent' | 'student' | 'school' | 'default';
}

const colorSchemes = {
  admin:     { primary: 'bg-admin-500 hover:bg-admin-600 text-white', secondary: 'bg-admin-50 text-admin-500 hover:bg-admin-100' },
  principal: { primary: 'bg-principal-500 hover:bg-principal-600 text-white', secondary: 'bg-principal-50 text-principal-500 hover:bg-principal-100' },
  teacher:   { primary: 'bg-teacher-500 hover:bg-teacher-600 text-white', secondary: 'bg-teacher-50 text-teacher-500 hover:bg-teacher-100' },
  parent:    { primary: 'bg-parent-500 hover:bg-parent-600 text-white', secondary: 'bg-parent-50 text-parent-500 hover:bg-parent-100' },
  student:   { primary: 'bg-student-500 hover:bg-student-600 text-white', secondary: 'bg-student-50 text-student-500 hover:bg-student-100' },
  // Reads live from the extracted school palette CSS vars (--school-dominant-*)
  // instead of a fixed portal color, and uses the same gradient (not a flat
  // fill) that the tab pill / date badge already use elsewhere in the app.
  school:    { primary: 'bg-[linear-gradient(135deg,var(--school-dominant-500)_0%,var(--school-dominant-600)_55%,var(--school-dominant-700)_100%)] hover:brightness-110 text-white', secondary: 'bg-[var(--school-dominant-50)] text-[var(--school-dominant-500)] hover:bg-[var(--school-dominant-100)]' },
  default:   { primary: 'bg-slate-800 hover:bg-slate-900 text-white', secondary: 'bg-slate-100 text-slate-700 hover:bg-slate-200' },
};

export function Button({
  variant = 'primary', size = 'md', loading = false, icon, iconPosition = 'left',
  fullWidth = false, colorScheme = 'default', className, children, disabled, ...props
}: ButtonProps) {
  const scheme = colorSchemes[colorScheme];
  const sizes = { sm: 'px-3 py-2 text-xs rounded-lg', md: 'px-5 py-2.5 text-sm rounded-xl', lg: 'px-6 py-3.5 text-sm rounded-xl' };
  const variants = {
    primary:   scheme.primary,
    secondary: scheme.secondary,
    ghost:     'bg-transparent text-text-secondary hover:bg-surface-3 hover:text-text-primary',
    danger:    'bg-red-500 hover:bg-red-600 text-white',
    success:   'bg-emerald-500 hover:bg-emerald-600 text-white',
  };

  return (
    <button
      className={clsx(
        'inline-flex items-center justify-center gap-2 font-semibold transition-all duration-200 active:scale-95',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
        sizes[size], variants[variant],
        fullWidth && 'w-full',
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (icon && iconPosition === 'left' && icon)}
      {children}
      {!loading && icon && iconPosition === 'right' && icon}
    </button>
  );
}

// ─── Input ───────────────────────────────────────────────────
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightElement?: React.ReactNode;
}

export function Input({ label, error, hint, leftIcon, rightElement, className, id, ...props }: InputProps) {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-text-primary mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none">
            {leftIcon}
          </div>
        )}
        <input
          id={inputId}
          className={clsx(
            'input-base',
            leftIcon && 'pl-10',
            rightElement && 'pr-10',
            error && 'border-red-400 focus:ring-red-400',
            className
          )}
          {...props}
        />
        {rightElement && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightElement}</div>
        )}
      </div>
      {error && <p className="mt-1.5 text-xs text-red-500 font-medium">{error}</p>}
      {hint && !error && <p className="mt-1.5 text-xs text-text-tertiary">{hint}</p>}
    </div>
  );
}

// ─── Select ──────────────────────────────────────────────────
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: { value: string | number; label: string; disabled?: boolean }[];
  placeholder?: string;
}

export function Select({ label, error, options, placeholder, className, id, ...props }: SelectProps) {
  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={selectId} className="block text-sm font-medium text-text-primary mb-1.5">
          {label}
        </label>
      )}
      <select
        id={selectId}
        className={clsx(
          'input-base appearance-none cursor-pointer',
          error && 'border-red-400 focus:ring-red-400',
          className
        )}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(opt => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>{opt.label}</option>
        ))}
      </select>
      {error && <p className="mt-1.5 text-xs text-red-500 font-medium">{error}</p>}
    </div>
  );
}

// ─── Card ────────────────────────────────────────────────────
interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

export function Card({ children, className, padding = 'md', onClick }: CardProps) {
  const paddings = { none: '', sm: 'p-4', md: 'p-5', lg: 'p-6' };
  return (
    <div
      className={clsx('card', paddings[padding], onClick && 'cursor-pointer hover:shadow-card-md transition-shadow', className)}
      onClick={onClick ? (e) => { if (!(e.target as HTMLElement).closest('button, [data-no-card-click]')) onClick(); } : undefined}
    >
      {children}
    </div>
  );
}

// ─── Badge ───────────────────────────────────────────────────
interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'top25' | 'upper' | 'lower' | 'bottom25';
  size?: 'sm' | 'md';
}

export function Badge({ children, variant = 'default', size = 'md' }: BadgeProps) {
  const variants = {
    default: 'bg-slate-100 text-slate-600',
    success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700',
    error:   'bg-red-100 text-red-700',
    info:    'bg-blue-100 text-blue-700',
    top25:   'bg-emerald-100 text-emerald-700',
    upper:   'bg-blue-100 text-blue-700',
    lower:   'bg-amber-100 text-amber-700',
    bottom25:'bg-red-100 text-red-700',
  };
  const sizes = { sm: 'text-xs px-2 py-0.5', md: 'text-xs px-2.5 py-1' };
  return (
    <span className={clsx('inline-flex items-center font-semibold rounded-full', variants[variant], sizes[size])}>
      {children}
    </span>
  );
}

// ─── Modal ───────────────────────────────────────────────────
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'full';
}

export function Modal({ open, onClose, title, description, children, size = 'md' }: ModalProps) {
  if (!open) return null;
  const sizes = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', full: 'max-w-full mx-4' };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={clsx(
        'relative bg-white w-full rounded-t-3xl sm:rounded-3xl shadow-2xl animate-slide-up',
        'max-h-[90vh] overflow-y-auto',
        sizes[size]
      )}>
        {title && (
          <div className="p-5 border-b border-border sticky top-0 bg-white rounded-t-3xl sm:rounded-t-3xl z-10">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-text-primary text-base">{title}</h3>
              <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-surface-3 transition-colors">
                <X className="w-4 h-4 text-text-secondary" />
              </button>
            </div>
            {description && <p className="text-xs text-text-secondary mt-1">{description}</p>}
          </div>
        )}
        <div className="p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ─── Sheet ───────────────────────────────────────────────────
interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
}

export function Sheet({ open, onClose, title, subtitle, children }: SheetProps) {
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg rounded-t-3xl shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto">
        {title && (
          <div className="p-5 border-b border-border sticky top-0 bg-white rounded-t-3xl z-10">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-text-primary text-base">{title}</h3>
              <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-surface-3 transition-colors">
                <X className="w-4 h-4 text-text-secondary" />
              </button>
            </div>
            {subtitle && <p className="text-xs text-text-secondary mt-1">{subtitle}</p>}
          </div>
        )}
        <div className="p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ─── Spinner ─────────────────────────────────────────────────
export function Spinner({ size = 'md', color = 'slate' }: { size?: 'sm' | 'md' | 'lg'; color?: string }) {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  return <Loader2 className={clsx(sizes[size], 'animate-spin', `text-${color}-500`)} />;
}

// ─── Empty State ─────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-surface-3 flex items-center justify-center mb-4 text-text-tertiary">
        {icon}
      </div>
      <h3 className="font-semibold text-text-primary mb-1">{title}</h3>
      {description && <p className="text-sm text-text-secondary mb-4">{description}</p>}
      {action}
    </div>
  );
}

// ─── Progress Bar ────────────────────────────────────────────
export function ProgressBar({ value, max = 100, color = 'blue', label }: {
  value: number; max?: number; color?: string; label?: string;
}) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const colors: Record<string, string> = {
    blue: 'bg-blue-500', green: 'bg-emerald-500', amber: 'bg-amber-500',
    red: 'bg-red-500', purple: 'bg-purple-500', teal: 'bg-teal-500',
  };
  return (
    <div className="w-full">
      {label && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-text-secondary">{label}</span>
          <span className="text-xs font-semibold text-text-primary">{pct}%</span>
        </div>
      )}
      <div className="h-2 bg-surface-3 rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all duration-500', colors[color] || 'bg-blue-500')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Stat Card ───────────────────────────────────────────────
export function StatCard({ label, value, icon, trend, color = 'blue', className }: {
  label: string; value: React.ReactNode; icon: React.ReactNode;
  trend?: { value: number; label: string }; color?: string; className?: string;
}) {
  // Most stat icons follow the school's extracted palette so they feel native
  // to each school's branding. Finance/revenue is the one exception — money
  // reads as green everywhere, regardless of school colors, so it gets a
  // fixed override via color="money" instead of the school-dominant default.
  const iconColorOverrides: Record<string, string> = {
    money: 'text-emerald-600',
  };
  return (
    <Card
      padding="sm"
      className={clsx(
        'group pt-2 transition-shadow duration-150',
        'active:shadow-[0_0_0_1.5px_var(--school-dominant-300),0_0_10px_1px_var(--school-dominant-200)]',
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-bold text-text-secondary truncate">{label}</p>
        <div
          className={clsx(
            'flex-shrink-0',
            iconColorOverrides[color] || 'text-school-dominant-500',
            '[&>svg]:w-4 [&>svg]:h-4 [&>svg]:stroke-[1.75]',
            'transition-[filter] duration-150 group-active:drop-shadow-[0_0_4px_currentColor]'
          )}
        >
          {icon}
        </div>
      </div>
      <p className="text-xl font-bold text-text-primary leading-tight mt-1">{value}</p>
      {trend && (
        <p className={clsx('text-xs font-medium mt-0.5', trend.value >= 0 ? 'text-emerald-600' : 'text-red-500')}>
          {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}% {trend.label}
        </p>
      )}
    </Card>
  );
}

// ─── Divider ─────────────────────────────────────────────────
export function Divider({ label }: { label?: string }) {
  if (!label) return <hr className="border-border my-4" />;
  return (
    <div className="flex items-center gap-3 my-4">
      <hr className="flex-1 border-border" />
      <span className="text-xs text-text-tertiary font-medium">{label}</span>
      <hr className="flex-1 border-border" />
    </div>
  );
}

// ─── Toggle ──────────────────────────────────────────────────
export function Toggle({ checked, onChange, label }: {
  checked: boolean; onChange: (v: boolean) => void; label?: string;
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <div
        className={clsx(
          'relative w-11 h-6 rounded-full transition-colors duration-200',
          checked ? 'bg-emerald-500' : 'bg-slate-200'
        )}
        onClick={() => onChange(!checked)}
      >
        <div className={clsx(
          'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200',
          checked && 'translate-x-5'
        )} />
      </div>
      {label && <span className="text-sm font-medium text-text-primary">{label}</span>}
    </label>
  );
}